import PageHeader from '../components/ui/PageHeader';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArticleService } from '../services/articleService';
import { formatScheduledDateShort } from '../utils/dateFormatter';
import { Article } from '../types';
import PageLoader from '../components/ui/PageLoader';
import { Button } from '../components/ui/Button';
import ArticleCard from '../components/ArticleCard';
import useArticleLiveUpdates from '../hooks/useArticleLiveUpdates';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import useAuthorNames from '../hooks/useAuthorNames';
import toast from 'react-hot-toast';
import { 
  DocumentTextIcon, 
  CalendarIcon
} from '@heroicons/react/24/outline';

export const AllArticlesPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  useArticleLiveUpdates(user, setArticles);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingArticleId, setDeletingArticleId] = useState<string | null>(null);
  const authorNames = useAuthorNames(articles, {
    enabled: hasRole(['administrator', 'redakteur']),
    fallbackLabel: 'Unbekannter Autor',
    onError: (error) => {
      console.error('Fehler beim Batch-Laden der Autoren:', error);
    }
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true); setLoadError('');
      try {
        // Nur Administratoren und Redakteure können diese Seite sehen
        if (!hasRole(['administrator', 'redakteur'])) {
          return;
        }

        // Alle Artikel laden (letzte 20)
        const recentArticles = await ArticleService.getAllArticles(user!, 20);
        setArticles(recentArticles);

      } catch (error) {
        setLoadError('Die Artikel konnten nicht geladen werden. Bitte versuche es erneut.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, hasRole, reload]);

  const handleDeleteArticle = async (articleId: string, articleTitle: string) => {
    setDeletingArticleId(articleId);

    try {
      await ArticleService.deleteArticleAsAdmin(articleId, user!);
      
      // Artikel aus der lokalen Liste entfernen
      setArticles(prevArticles => prevArticles.filter(article => article.id !== articleId));
      
      toast.success('Artikel erfolgreich gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen des Artikels:', error);
      toast.error('Fehler beim Löschen des Artikels');
    } finally {
      setDeletingArticleId(null);
      setDeleteTarget(null);
    }
  };

  if (!hasRole(['administrator', 'redakteur'])) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <PageHeader title={<>Zugriff verweigert</>} description={<>Du hast keine Berechtigung, diese Seite zu sehen.</>} />
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <div>
            <PageHeader title={<>Alle Artikel</>} description={<>Die letzten 20 Artikel aller Autoren</>} />
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <CalendarIcon className="w-4 h-4 mr-1" />
            {articles.length} Artikel
          </div>
        </div>
      </div>

      {loadError && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">{loadError}
        <Button variant="outline" onClick={() => setReload(n => n + 1)} className="ml-3">Erneut versuchen</Button></div>}
      <ConfirmDialog open={!!deleteTarget} title="Artikel löschen" description={`Soll der Artikel „${deleteTarget?.title || ''}“ endgültig gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Endgültig löschen" danger busy={!!deletingArticleId} onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) void handleDeleteArticle(deleteTarget.id, deleteTarget.title); }} />
      {/* Artikel-Liste */}
      <div className="card">
        {loadError ? null : articles.length === 0 ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Noch keine Artikel vorhanden</p>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                variant="allArticles"
                authorName={authorNames[article.authorId]}
                onDelete={(id, title) => setDeleteTarget({ id, title })}
                deleting={deletingArticleId === article.id}
                formatScheduled={formatScheduledDateShort}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
